import type { Options as KyOptions } from "ky";
import { kyClient } from "lib/ky";
import { REGION_ORIGINS, type RegionIds } from "utils/api-client/regions";
import { getAuthToken } from "utils/auth";

/* Initialise based on first URL, but then <APIRegionListener /> will continually
   update this value when URL changes. */
let currentBaseOrigin =
  REGION_ORIGINS[getFirstPathParam(window.location.href) as RegionIds] ||
  REGION_ORIGINS.local;

function setBaseOrigin(url: string) {
  currentBaseOrigin = url.replace(/\/$/, ""); // strip trailing slash if exists
}

function getBaseUrl() {
  return (
    currentBaseOrigin +
    (import.meta.env.VITE_API_PATH.startsWith("/")
      ? import.meta.env.VITE_API_PATH
      : "/" + import.meta.env.VITE_API_PATH
    ).replace(/\/$/, "")
  );
}

async function makeKyRequest<R>({
  method,
  url,
  json,
  searchParams,
}: {
  method: string;
  url: string;
  json?: KyOptions["json"];
  searchParams?: KyOptions["searchParams"];
}) {
  url = url[0] === "/" ? url.replace(/^\//, "") : url;
  return (await kyClient(url, {
    prefixUrl: getBaseUrl(),
    method,
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
    },
    searchParams,
    json,
  }).json()) as R;
}

export const apiClient = {
  get: <R>(url: string, searchParams?: KyOptions["searchParams"]) =>
    makeKyRequest<R>({ method: "GET", url, searchParams }),
  post: <R>(url: string, json: KyOptions["json"]) =>
    makeKyRequest<R>({ method: "POST", url, json }),
  put: <R>(url: string, json: KyOptions["json"]) =>
    makeKyRequest<R>({ method: "PUT", url, json }),
  patch: <R>(url: string, json: KyOptions["json"]) =>
    makeKyRequest<R>({ method: "PATCH", url, json }),
  delete: <R>(url: string, json?: KyOptions["json"]) =>
    makeKyRequest<R>({ method: "DELETE", url, json }),
  upload: async <R>(
    url: string,
    file: File | File[],
    body: Record<string, any> = {},
  ) => {
    url = url[0] === "/" ? url.replace(/^\//, "") : url;

    const formData = new FormData();

    if (Array.isArray(file)) {
      file.forEach((f) => formData.append("files[]", f));
    } else {
      formData.append("file", file);
    }

    Object.entries(body).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    const response = await fetch(getBaseUrl() + "/" + url, {
      method: "POST",
      body: formData,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Upload failed with status: ${response.status}`,
      );
    }

    return (await response.json()) as R;
  },
  setBaseOrigin: setBaseOrigin,
  getBaseOrigin: () => currentBaseOrigin,
  getBaseUrl: getBaseUrl,
};

function getFirstPathParam(url: string): string | null {
  try {
    const path = new URL(url).pathname;
    const segments = path.split("/").filter(Boolean);
    return segments[0] || null;
  } catch (err) {
    return null;
  }
}
